import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Play, Square, Timer } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import DataTable from "components/common/DataTable";

import {
  fetchBreakLogs,
  createBreakLog,
  punchOutBreakLog,
  exportBreakLogs,
} from "store/slices/breakTimeLogSlice";

import useCanteenSocket from "hooks/useCanteenSocket";
import useNotificationSocket from "hooks/useNotificationSocket";
import usePermissionSocket from "hooks/usePermissionSocket";
import { usePermissions } from "hooks/usePermissions";
import { toast } from "sonner";
import { formatDateTime } from "utlis/formateDateTime";

const Attendance = () => {
  usePermissionSocket();
  useCanteenSocket();
  useNotificationSocket();

  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const currentUserId = currentUser?.id || localStorage.getItem("userId");

  const [isAttendanceModalOpen, setIsAttendanceModalOpen] = useState(false);
  //   const [attendancePage, setAttendancePage] = useState(1);
  //   const [attendancePageSize, setAttendancePageSize] = useState(10);
  const [attendanceSearch, setAttendanceSearch] = useState("");

  const [modalLogs, setModalLogs] = useState([]);
  //   const [modalPagination, setModalPagination] = useState({ count: 0, page_size: 1000000 });
  const [modalLoading, setModalLoading] = useState(false);

  const [isDayDetailOpen, setIsDayDetailOpen] = useState(false);
  const [selectedDayLogs, setSelectedDayLogs] = useState([]);
  const [selectedDayLabel, setSelectedDayLabel] = useState("");

  const { canExport: canExportBreakLogs } = usePermissions("break_time_log");
  const { canRead } = usePermissions("dashboard");

  const { logs: allBreakLogs = [] } = useSelector(
    (state) => state.breakTimeLog || { logs: [] }
  );

  /* ================= FETCH ALL LOGS FOR DASHBOARD ================= */

  useEffect(() => {
    if (currentUserId) {
      dispatch(
        fetchBreakLogs({ title: "attendance", employee: currentUserId })
      );
    }
  }, [dispatch, currentUserId]);

  const userBreakLogs = useMemo(() => {
    return allBreakLogs.filter((log) => log.employee === currentUserId);
  }, [allBreakLogs, currentUserId]);

  const activeBreak = useMemo(() => {
    return userBreakLogs.find((log) => !log.punch_out);
  }, [userBreakLogs]);

  /* ================= GROUP FOR DASHBOARD ================= */

  const groupedDailyLogs = useMemo(() => {
    const map = {};

    userBreakLogs.forEach((log) => {
      const dayKey = new Date(log.punch_in).toDateString();

      if (!map[dayKey]) {
        map[dayKey] = {
          date: dayKey,
          punch_in: log.punch_in,
          punch_out: log.punch_out,
        };
      } else {
        if (new Date(log.punch_in) < new Date(map[dayKey].punch_in)) {
          map[dayKey].punch_in = log.punch_in;
        }

        if (log.punch_out) {
          if (
            !map[dayKey].punch_out ||
            new Date(log.punch_out) > new Date(map[dayKey].punch_out)
          ) {
            map[dayKey].punch_out = log.punch_out;
          }
        }
      }
    });

    return Object.values(map).sort(
      (a, b) => new Date(b.punch_in) - new Date(a.punch_in)
    );
  }, [userBreakLogs]);

  const dashboardAttendance = useMemo(() => {
    return groupedDailyLogs.slice(0, 5);
  }, [groupedDailyLogs]);

  /* ================= CLOCK IN / OUT ================= */

  const handleCheckIn = () => {
    dispatch(
      createBreakLog({
        punch_in: new Date().toISOString(),
        title: "attendance",
      })
    );
  };

  const handleCheckOut = () => {
    if (!activeBreak) return;

    dispatch(
      punchOutBreakLog({
        id: activeBreak.id,
        punch_out: new Date().toISOString(),
        title: "attendance",
      })
    );
  };

  /* ================= MODAL FETCH ================= */

  const refetchAttendanceModal = useCallback(() => {
    setModalLoading(true);
    dispatch(
      //   fetchBreakLogs({
      //     page: attendancePage,
      //     pageSize: attendancePageSize,
      //     search: attendanceSearch,
      //     title: 'attendance',
      //     employee: currentUserId,
      //   })

      fetchBreakLogs({
        search: attendanceSearch,
        title: "attendance",
        employee: currentUserId,
      })
    )
      .unwrap()
      .then((res) => {
        setModalLogs(res.data || []);
        // setModalPagination(res.pagination || { count: 0, page_size: 10 });
      })
      .catch(() => toast.error("Failed to load attendance"))
      .finally(() => setModalLoading(false));
  }, [
    dispatch,
    // attendancePage,
    // attendancePageSize,
    attendanceSearch,
    currentUserId,
  ]);

  useEffect(() => {
    if (isAttendanceModalOpen) {
      refetchAttendanceModal();
    }
  }, [isAttendanceModalOpen, refetchAttendanceModal]);

  /* ================= GROUP MODAL DATA (ONE ROW PER DAY) ================= */

  const groupedModalLogs = useMemo(() => {
    const map = {};

    modalLogs.forEach((log) => {
      const dayKey = new Date(log.punch_in).toDateString();

      if (!map[dayKey]) {
        map[dayKey] = {
          date: dayKey,
          punch_in: log.punch_in,
          punch_out: log.punch_out,
          logs: [log],
        };
      } else {
        map[dayKey].logs.push(log);

        if (new Date(log.punch_in) < new Date(map[dayKey].punch_in)) {
          map[dayKey].punch_in = log.punch_in;
        }

        if (log.punch_out) {
          if (
            !map[dayKey].punch_out ||
            new Date(log.punch_out) > new Date(map[dayKey].punch_out)
          ) {
            map[dayKey].punch_out = log.punch_out;
          }
        }
      }
    });

    return Object.values(map).sort(
      (a, b) => new Date(b.punch_in) - new Date(a.punch_in)
    );
  }, [modalLogs]);

  const handleExportAttendance = () => {
    if (!canExportBreakLogs) {
      toast.error("You don't have permission");
      return;
    }
    dispatch(exportBreakLogs({ title: "attendance" }));
  };

  if (!canRead) return null;

  return (
    <div className="space-y-8">
      {/* ================= DASHBOARD CARD ================= */}
      <Card>
        <CardHeader className="flex flex-row justify-between">
          <div>
            <CardTitle className="flex gap-2">
              <Timer className="w-5 h-5" />
              My Attendance
            </CardTitle>
            <CardDescription>Clock-In / Clock-Out</CardDescription>
          </div>
          <button
            onClick={() => setIsAttendanceModalOpen(true)}
            className="text-primary text-sm hover:underline"
          >
            View All
          </button>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="flex gap-4">
            {!activeBreak ? (
              <Button onClick={handleCheckIn}>
                <Play className="w-4 h-4 mr-2" /> Clock-In
              </Button>
            ) : (
              <Button variant="destructive" onClick={handleCheckOut}>
                <Square className="w-4 h-4 mr-2" /> Clock-Out
              </Button>
            )}
          </div>
          {activeBreak && (
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-emerald-700">
                  Currently Clocked In
                </span>
                <span className="text-xs text-emerald-600">
                  Started at:{" "}
                  {new Date(activeBreak.punch_in).toLocaleTimeString()}
                </span>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {dashboardAttendance.slice(0, 3).map((log, i) => (
              <div key={i} className="flex justify-between border rounded p-3">
                <div>
                  <p className="font-medium">{log.date}</p>
                  <p className="text-xs">
                    In: {formatDateTime(log.punch_in)} | Out:{" "}
                    {formatDateTime(log.punch_out)
                      ? formatDateTime(log.punch_out)
                      : "—"}
                  </p>
                </div>
                <Badge>{log.punch_out ? "Completed" : "Active"}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ================= ATTENDANCE MODAL ================= */}
      <Dialog
        open={isAttendanceModalOpen}
        onOpenChange={setIsAttendanceModalOpen}
      >
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>My Attendance History</DialogTitle>
            <DialogDescription>One entry per day</DialogDescription>
          </DialogHeader>

          <DataTable
            data={groupedModalLogs}
            isLoading={modalLoading}
            // pageIndex={attendancePage - 1}
            // pageSize={attendancePageSize}
            // pageCount={Math.ceil(modalPagination.count / modalPagination.page_size)}
            // onPageChange={i => setAttendancePage(i + 1)}
            // onPageSizeChange={setAttendancePageSize}
            showSearch={false}
            manualPagination={false}
            disablePagination
            onSearch={setAttendanceSearch}
            showExport
            onExport={handleExportAttendance}
            showImport={false}
            columns={[
              {
                header: "Date",
                cell: ({ row }) => row.original.date,
              },
              {
                header: "First In",
                cell: ({ row }) => formatDateTime(row.original.punch_in),
              },
              {
                header: "Last Out",

                cell: ({ row }) =>
                  formatDateTime(row.original.punch_out)
                    ? formatDateTime(row.original.punch_out)
                    : "—",
              },
              {
                header: "Details",
                cell: ({ row }) => (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedDayLogs(row.original.logs);
                      setSelectedDayLabel(row.original.date);
                      setIsDayDetailOpen(true);
                    }}
                  >
                    View Details
                  </Button>
                ),
              },
            ]}
          />
        </DialogContent>
      </Dialog>

      {/* ================= DAY DETAILS MODAL ================= */}
      <Dialog open={isDayDetailOpen} onOpenChange={setIsDayDetailOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Attendance Details</DialogTitle>
            <DialogDescription>{selectedDayLabel}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {selectedDayLogs.map((log, i) => (
              <div key={i} className="flex justify-between border rounded p-3">
                <span>In: {formatDateTime(log.punch_in)}</span>
                <span>
                  Out: {log.punch_out ? formatDateTime(log.punch_out) : "—"}
                </span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Attendance;
